// Bethesda plugin activation for the web build.
//
// Deploying a mod copies its files (incl. .esp/.esm/.esl) into the game
// folder, but Bethesda games only LOAD plugins listed in plugins.txt, which
// lives in %LOCALAPPDATA%\<game>\ — a different folder from the game install.
// The desktop gamebryo-plugin-management extension (which writes plugins.txt)
// is a dynamic extension and is fused off in the web build, so nothing
// activates the plugin.
//
// This writes plugins.txt itself, into a separate File System Access mount
// the user grants on their AppData/Local/<game> folder, registered at the
// virtual path /plugins/<gameId> (see fsa-mounts.requestPluginsFolder). The
// copy activator calls syncAfterDeploy() after each deploy.
const path = require("path");

const core = require("./fsa-core.cjs");

// gameId -> { appData: AppData/Local subfolder, asterisk: SSE/FO4 "*"-format }
// (values mirror gamebryo-plugin-management/src/util/gameSupport.ts)
const BETHESDA = {
    skyrimspecialedition: { appData: "Skyrim Special Edition", asterisk: true },
    skyrim: { appData: "Skyrim", asterisk: false },
    skyrimvr: { appData: "Skyrim VR", asterisk: true },
    enderalspecialedition: {
        appData: "Enderal Special Edition",
        asterisk: true,
    },
    fallout4: { appData: "Fallout4", asterisk: true },
    fallout4vr: { appData: "Fallout4VR", asterisk: true },
    fallout3: { appData: "Fallout3", asterisk: false },
    falloutnv: { appData: "falloutnv", asterisk: false },
    oblivion: { appData: "oblivion", asterisk: false },
    starfield: { appData: "Starfield", asterisk: true },
};

const PLUGIN_EXT = new Set([".esp", ".esm", ".esl"]);

function isBethesda(gameId) {
    return BETHESDA[gameId] !== undefined;
}

function appDataName(gameId) {
    return BETHESDA[gameId]?.appData;
}

function pluginsMountPath(gameId) {
    return `/plugins/${gameId}`;
}

// Resolve the actual plugins.txt path, matching whatever casing already
// exists in the folder. The game (esp. via Proton/Wine on a case-sensitive
// Linux filesystem) may use "Plugins.txt"; writing a fresh lowercase
// "plugins.txt" would create a SECOND file the game ignores. Match-existing
// avoids the duplicate and updates the file the game actually reads.
async function pluginsTxtPath(gameId) {
    const dir = `/plugins/${gameId}`;
    let names = [];
    try {
        const result = await core.readdir(dir);
        if (Array.isArray(result)) names = result;
    } catch (err) {
        /* dir not listable yet */
    }
    const variants = names.filter((n) => n.toLowerCase() === "plugins.txt");
    if (variants.length === 0) {
        return `${dir}/plugins.txt`;
    }
    if (variants.length === 1) {
        return `${dir}/${variants[0]}`;
    }
    // multiple case variants (only possible on a case-sensitive fs): update
    // the one with the most entries (the game's real load order)
    let best = variants[0];
    let bestLines = -1;
    for (const variant of variants) {
        let count = 0;
        try {
            const data = await core.readFile(`${dir}/${variant}`, "utf8");
            if (data !== core.NOT_HANDLED) {
                count = String(data)
                    .split(/\r?\n/)
                    .filter((l) => l.trim().length > 0).length;
            }
        } catch (err) {
            /* unreadable */
        }
        if (count > bestLines) {
            bestLines = count;
            best = variant;
        }
    }
    console.warn(
        `[vortex-web] multiple plugins.txt case variants in ${dir} ` +
            `(${variants.join(", ")}); updating "${best}" — remove the others`,
    );
    return `${dir}/${best}`;
}

// the plugins this build has activated (basenames), per game. Repopulated from
// each deploy's manifest; merged into plugins.txt (we add, we don't remove).
const desiredActive = new Map();

function collectPlugins(relPaths) {
    const names = new Set();
    for (const rel of relPaths || []) {
        const base = path.basename(String(rel).replace(/\\/g, "/"));
        if (PLUGIN_EXT.has(path.extname(base).toLowerCase())) {
            names.add(base);
        }
    }
    return names;
}

function hasLiveMount(gameId) {
    const mountPath = pluginsMountPath(gameId);
    return core
        .listMounts()
        .some((m) => m.mountPath === mountPath && m.state === "live");
}

// merge the desired-active plugins into plugins.txt (preserving the user's
// existing entries and order), then write it through the plugins mount
async function writeMerged(gameId) {
    const cfg = BETHESDA[gameId];
    const want = desiredActive.get(gameId);
    if (cfg === undefined || want === undefined || want.size === 0) {
        return;
    }

    const txtPath = await pluginsTxtPath(gameId);
    let existing = "";
    try {
        const data = await core.readFile(txtPath, "utf8");
        if (data !== core.NOT_HANDLED) {
            existing = String(data);
        }
    } catch (err) {
        // no existing plugins.txt yet
    }

    const lines = existing.split(/\r?\n/).filter((l) => l.trim().length > 0);
    const present = new Set(
        lines.map((l) => l.replace(/^\*/, "").trim().toLowerCase()),
    );
    for (const name of want) {
        if (!present.has(name.toLowerCase())) {
            lines.push((cfg.asterisk ? "*" : "") + name);
            present.add(name.toLowerCase());
        }
    }

    await core.writeFile(txtPath, lines.join("\r\n") + "\r\n");
    console.info(
        `[vortex-web] activated ${want.size} plugin(s) in plugins.txt for ${gameId}`,
    );
}

/**
 * Called by the copy activator after a deploy. Records the deployed plugins
 * and either writes plugins.txt (if the AppData folder is already granted) or
 * asks fsa-mounts to prompt for it on the next user gesture.
 */
async function syncAfterDeploy(gameId, relPaths) {
    if (!isBethesda(gameId)) {
        return;
    }
    const plugins = collectPlugins(relPaths);
    if (plugins.size > 0) {
        desiredActive.set(gameId, plugins);
    }

    if (hasLiveMount(gameId)) {
        if (plugins.size > 0) {
            await writeMerged(gameId);
        }
    } else {
        // Prompt for the AppData/Local/<game> folder (where plugins.txt lives)
        // as soon as a Bethesda game is managed — even before any plugin is
        // installed. fsa-mounts shows a button; granting flushes any pending
        // plugins.txt. Bare activation deploys also reach here, so the prompt
        // appears right after the game is managed.
        globalThis.__vortexWebPromptPluginsFolder?.(gameId);
    }
}

/** Called by fsa-mounts once the plugins folder is granted/unlocked. */
async function flush(gameId) {
    if (hasLiveMount(gameId)) {
        await writeMerged(gameId);
    }
}

module.exports = {
    isBethesda,
    appDataName,
    pluginsMountPath,
    syncAfterDeploy,
    flush,
};
globalThis.__vortexWebPlugins = module.exports;
